import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { brokerAccounts, type BrokerAccountCreate } from "@/api/brokerAccounts";
import { Badge, Button, Card, CardBody, CardHeader, FormError, Input, Label, Select } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";

/**
 * Per-broker required-field map. Keys are broker identifiers; values are the
 * names of the credential fields the form should expose. Stays in sync with
 * `broker/factories.py` on the backend.
 */
const BROKER_FIELDS: Record<string, { label: string; placeholder?: string }[]> = {
  alpaca: [
    { label: "api_key" },
    { label: "secret_key" },
    { label: "api_url", placeholder: "https://paper-api.alpaca.markets" },
  ],
  oanda: [
    { label: "api_token" },
    { label: "account_id" },
    { label: "api_url", placeholder: "https://api-fxpractice.oanda.com" },
  ],
  "ccxt:binance": [
    { label: "api_key" },
    { label: "secret" },
    { label: "sandbox", placeholder: "true" },
  ],
  "ccxt:kraken": [
    { label: "api_key" },
    { label: "secret" },
    { label: "sandbox", placeholder: "false" },
  ],
};

export function BrokerAccountsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ["broker-accounts"], queryFn: () => brokerAccounts.list() });
  const [open, setOpen] = useState(false);

  const revoke = useMutation({
    mutationFn: (args: { broker: string; label: string }) =>
      brokerAccounts.revoke(args.broker, args.label),
    onSuccess: () => {
      toast.push("Broker account revoked", "success");
      qc.invalidateQueries({ queryKey: ["broker-accounts"] });
    },
  });

  const test = useMutation({
    mutationFn: (args: { broker: string; label: string }) =>
      brokerAccounts.test(args.broker, args.label),
    onSuccess: (r) => toast.push(r.ok ? "Credentials decrypt ok" : `Test failed: ${r.detail}`, r.ok ? "success" : "error"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Broker accounts</h1>
        <Button onClick={() => setOpen(true)}>Add broker</Button>
      </div>
      <Card>
        <CardBody className="overflow-x-auto">
          {list.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : list.data?.length === 0 ? (
            <p className="text-sm text-slate-500">
              No broker accounts. Add one to route orders to your real broker — without one, paper-trading is used.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Broker</th>
                  <th>Label</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 font-medium">{a.broker}</td>
                    <td>{a.account_label}</td>
                    <td>
                      <Badge tone={a.mode === "live" ? "warn" : "neutral"}>{a.mode}</Badge>
                    </td>
                    <td>
                      <Badge tone={a.status === "active" ? "good" : "bad"}>{a.status}</Badge>
                    </td>
                    <td className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
                    <td>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => test.mutate({ broker: a.broker, label: a.account_label })}
                          loading={test.isPending}
                        >
                          Test
                        </Button>
                        <Button
                          variant="danger"
                          disabled={a.status !== "active"}
                          onClick={() => {
                            if (confirm(`Revoke ${a.broker}/${a.account_label}?`)) {
                              revoke.mutate({ broker: a.broker, label: a.account_label });
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      <AddBrokerModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function AddBrokerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [broker, setBroker] = useState<string>(Object.keys(BROKER_FIELDS)[0]);
  const [label, setLabel] = useState("paper");
  const [mode, setMode] = useState<"paper" | "live">("paper");
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const fields = BROKER_FIELDS[broker] ?? [];

  const submit = useMutation({
    mutationFn: (body: BrokerAccountCreate) => brokerAccounts.create(body),
    onSuccess: () => {
      toast.push("Broker account saved", "success");
      qc.invalidateQueries({ queryKey: ["broker-accounts"] });
      onClose();
      setCreds({});
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add broker account">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          submit.mutate({ broker, account_label: label, mode, credentials: creds });
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Broker</Label>
            <Select value={broker} onChange={(e) => setBroker(e.target.value)}>
              {Object.keys(BROKER_FIELDS).map((b) => (
                <option key={b}>{b}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="paper" />
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={mode} onChange={(e) => setMode(e.target.value as "paper" | "live")}>
              <option value="paper">paper</option>
              <option value="live">live</option>
            </Select>
          </div>
        </div>

        <div className="space-y-2 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Credentials</div>
          {fields.map((f) => (
            <div key={f.label}>
              <Label>{f.label}</Label>
              <Input
                type={f.label.includes("secret") || f.label.includes("token") || f.label.includes("key") ? "password" : "text"}
                placeholder={f.placeholder}
                value={creds[f.label] ?? ""}
                onChange={(e) => setCreds({ ...creds, [f.label]: e.target.value })}
              />
            </div>
          ))}
          <p className="text-xs text-slate-500">
            Credentials are encrypted at rest with a per-org key. They never appear in logs or API responses.
          </p>
        </div>

        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submit.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
